import { ref, type Ref } from "vue";
import { Capacitor, registerPlugin } from "@capacitor/core";

import { isAndroid, isIOS, isNativePlatform } from "./platform";

export type NotificationPermissionStatus = "unknown" | "granted" | "prompt" | "denied";

interface NotificationSettingsPlugin {
  open(): Promise<void>;
}

interface AppSettingsPlugin {
  openAppSettings(): Promise<{ opened: boolean }>;
}

const NotificationSettings = registerPlugin<NotificationSettingsPlugin>("NotificationSettings");
// iOS-only custom plugin that wraps UIApplication.openSettingsURLString.
// On Android the call is unimplemented and we instead use the Android-only
// NotificationSettings plugin above.
const AppSettings = registerPlugin<AppSettingsPlugin>("AppSettings");

const AUTO_REQUEST_FLAG_KEY = "knocknoc_notification_auto_requested";

export const notificationPermission: Ref<NotificationPermissionStatus> = ref("unknown");

let _watcherInitialized = false;

function mapPermission(display: string | undefined): NotificationPermissionStatus {
  if (display === "granted") return "granted";
  if (display === "denied") return "denied";
  return "prompt";
}

// Avoid "plugin is not implemented" exceptions when the bridge isn't ready yet.
function isLocalNotificationsAvailable(): boolean {
  if (!isNativePlatform()) return true;
  return Capacitor.isPluginAvailable("LocalNotifications");
}

export async function checkNotificationPermission(): Promise<void> {
  if (!isNativePlatform()) {
    notificationPermission.value = "granted";
    return;
  }
  if (!isLocalNotificationsAvailable()) {
    // Don't flip to "prompt": the bridge may just be late. Avoid false-positive overlay.
    console.warn("[notification-permissions] LocalNotifications plugin not available yet");
    return;
  }
  try {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    const perms = await LocalNotifications.checkPermissions();
    notificationPermission.value = mapPermission(perms.display);
  } catch (err) {
    // Failed check is not evidence of denial; leave state as-is.
    console.warn("[notification-permissions] checkPermissions failed", err);
  }
}

export async function requestNotificationPermission(): Promise<void> {
  if (!isNativePlatform()) return;
  if (!isLocalNotificationsAvailable()) {
    // Plugin unreachable mid-prompt → fall through to OS settings.
    console.warn("[notification-permissions] LocalNotifications unavailable, opening OS settings");
    await openNotificationSettings();
    return;
  }
  try {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    const perms = await LocalNotifications.requestPermissions();
    notificationPermission.value = mapPermission(perms.display);
  } catch (err) {
    console.warn("[notification-permissions] requestPermissions failed, falling back to settings", err);
    await openNotificationSettings();
  }
}

export async function openNotificationSettings(): Promise<void> {
  if (!isNativePlatform()) return;
  if (isAndroid()) {
    try {
      await NotificationSettings.open();
    } catch {
      // Fall through; appStateChange listener will recheck when user returns.
    }
    return;
  }
  if (isIOS()) {
    // iOS: deep-link into Settings → Knocknoc via our custom AppSettings
    // plugin (UIApplication.openSettingsURLString). @capacitor/browser only
    // accepts http(s) and silently rejects "app-settings:".
    try {
      await AppSettings.openAppSettings();
    } catch (e) {
      console.warn("[notification-permissions] iOS open settings failed", e);
    }
  }
}

async function hasAutoRequested(): Promise<boolean> {
  try {
    const { Preferences } = await import("@capacitor/preferences");
    const { value } = await Preferences.get({ key: AUTO_REQUEST_FLAG_KEY });
    return value === "true";
  } catch {
    return false;
  }
}

async function markAutoRequested(): Promise<void> {
  try {
    const { Preferences } = await import("@capacitor/preferences");
    await Preferences.set({ key: AUTO_REQUEST_FLAG_KEY, value: "true" });
  } catch {
    // Non-fatal: next launch will re-attempt the prompt.
  }
}

export async function initNotificationPermissionWatcher(): Promise<void> {
  if (!isNativePlatform() || _watcherInitialized) return;
  _watcherInitialized = true;

  await checkNotificationPermission();

  // Fire the system dialog on first launch so users aren't gated behind the
  // overlay. After the first decision, requestPermissions() is a no-op on
  // Android, so later launches rely on the overlay to route to settings.
  if (notificationPermission.value === "prompt" && !(await hasAutoRequested())) {
    await markAutoRequested();
    await requestNotificationPermission();
  }

  const { App } = await import("@capacitor/app");
  // Re-check on foreground so toggles in system settings take effect.
  await App.addListener("appStateChange", (state) => {
    if (state.isActive) {
      checkNotificationPermission();
    }
  });
}
